import React from 'react';
import { Link, Image } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { TitleMeta } from './title-meta';
import { TitlePreview } from './title-preview';

const LANDSCAPE_SIZES = '(max-width: 640px) 72vw, (max-width: 1024px) 40vw, 24vw';
const POSTER_SIZES = '(max-width: 640px) 42vw, (max-width: 1024px) 28vw, 17vw';
const REVEAL_DELAY = 450;


/**
 * variant="poster" (default): tall 2:3 card for catalogue rows/grids, Netflix-style.
 * variant="landscape": 16:9 card, kept for "Continue Watching" where a frame from the film reads better.
 * A title without a poster falls back to its landscape thumbnail (centre-cropped), and vice-versa.
 */
export function TitleCard({ title, rank, className, priority = false, variant = 'poster' }) {
  const isPoster = variant === 'poster';
  const ranked = typeof rank === 'number';
  const frameRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const timer = React.useRef(null);
  const [anchor, setAnchor] = React.useState(null);

  const id = title.id || title.Id || title.MovieId;
  const name = title.title || title.Title || title.Name || 'Title';
  const landscapeArt = title.thumbnail || title.ThumbnailUrl || title.BannerUrl || title.CoverUrl;
  const posterArt = title.poster || title.PosterUrl;
  const thumbnail = isPoster ? (posterArt || landscapeArt) : landscapeArt;
  // Static card stays single-category for a clean, uncluttered line — the full category
  // list only shows up on hover (TitlePreview) and on the title details page.
  const category = title.category || title.CategoryName || title.Genre;
  const year = title.year || title.ReleaseYear;

  const cancel = React.useCallback(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  React.useEffect(() => cancel, [cancel]);

  const arm = (event) => {
    if (event.pointerType !== 'mouse') return;
    cancel();
    timer.current = window.setTimeout(() => {
      const frame = frameRef.current;
      if (frame) setAnchor(frame.getBoundingClientRect());
    }, REVEAL_DELAY);
  };

  React.useEffect(() => {
    if (!anchor) return;
    const dismiss = () => setAnchor(null);

    const track = (event) => {
      const over = (node) => {
        const rect = node?.getBoundingClientRect();
        if (!rect) return false;
        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        );
      };
      if (!over(frameRef.current) && !over(previewRef.current)) {
        dismiss();
      }
    };

    window.addEventListener('pointermove', track);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('pointermove', track);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [anchor]);

  return (
    <div
      ref={frameRef}
      onPointerEnter={arm}
      onPointerLeave={cancel}
      className={cn('group/card relative block cursor-pointer', className)}
    >
      <Link
        href={`/movie/${id}`}
        aria-label={ranked ? `Rank ${rank}, ${name}` : name}
        className="block focus-visible:outline-none"
      >
        <div className={cn('relative', ranked && 'pl-8 sm:pl-10 lg:pl-12')}>
          {ranked && (
            <span
              className="pointer-events-none absolute left-0 bottom-0 z-0 text-7xl sm:text-8xl lg:text-9xl font-black leading-none select-none tracking-tighter drop-shadow-[0_8px_20px_rgba(0,0,0,0.95)]"
              style={{
                fontFamily: "'Impact', 'Arial Black', -apple-system, sans-serif",
                color: 'var(--ember, #ff7a00)',
                WebkitTextStroke: '2px rgba(0, 0, 0, 0.9)',
                paintOrder: 'stroke fill',
                letterSpacing: rank === 10 ? '-0.08em' : '-0.04em',
              }}
            >
              {rank}
            </span>
          )}

          <div className={cn(
            'relative z-10 overflow-hidden rounded-xl bg-[#110e1b] ring-1 ring-white/10 transition-all duration-300 group-hover/card:ring-white/25 group-hover/card:shadow-xl group-hover/card:shadow-black/50',
            isPoster ? 'aspect-[2/3]' : 'aspect-video'
          )}>
            {thumbnail ? (
              <Image
                src={thumbnail}
                alt={name}
                fill
                priority={priority}
                sizes={isPoster ? POSTER_SIZES : LANDSCAPE_SIZES}
                className="object-cover transition-transform duration-300 group-hover/card:scale-105"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-white/5">
                <Icon icon="ph:film-slate" className="size-8 text-muted-foreground" />
              </div>
            )}

            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 transition-opacity duration-200 group-hover/card:opacity-100" />
          </div>
        </div>

        <div className={cn('mt-2.5 px-0.5', ranked && 'pl-8 sm:pl-10 lg:pl-12')}>
          <p className={cn(
            'truncate font-semibold text-foreground group-hover/card:text-white transition-colors',
            isPoster ? 'text-sm sm:text-base' : 'text-lg'
          )}>
            {name}
          </p>
          <TitleMeta
            items={[category, year]}
            className={cn('mt-0.5 text-muted-foreground', isPoster ? 'text-xs sm:text-sm' : 'text-base')}
          />
        </div>
      </Link>

      {anchor && (
        <TitlePreview title={title} anchor={anchor} previewRef={previewRef} />
      )}
    </div>
  );
}

export default TitleCard;
